import { significantTokens } from '../../json/lexer';
import { ScanErrorKind, type Token, TokenKind } from '../../json/tokens';
import type { LintCheck, LintInput } from '../check';
import type { DiagnosticDraft, Span } from '../diagnostic';

/**
 * Everything that can be decided from the token stream alone.
 *
 * This is the check the legacy fixer never had. Its whole premise is that a *lexer* already
 * decided where every string literal starts and ends, so "a comma before `}`" means a
 * `Comma` token whose next significant token is `CloseBrace` — a statement that cannot be
 * true inside `{"a":"x, }"}`, where the scanner produced one `String` token and no comma at
 * all. The four measured corruptions of the regex pipeline (a `//` in a URL, a `,}` in a
 * value, typographic quotes in prose, an unquoted-key pattern inside a sentence) are not
 * fixed here so much as inexpressible (ADR-0007, ADR-0011).
 *
 * It owns, deliberately, every code that jsonc's own error stream also reports lexically —
 * `InvalidSymbol`, `UnexpectedEndOfString`, `InvalidEscapeCharacter`, `InvalidUnicode`,
 * `InvalidCharacter`, `UnexpectedEndOfNumber`. `checks/structure.ts` drops those raw errors
 * precisely because this check says the same thing better: anchored on the two-character
 * escape rather than on the whole string, and with the token text as a parameter.
 */

/** `{` `[` `,` `:` before a comma mean there is no value in front of it. */
const NO_VALUE_BEFORE: ReadonlySet<TokenKind> = new Set<TokenKind>([
  TokenKind.OpenBrace,
  TokenKind.OpenBracket,
  TokenKind.Comma,
  TokenKind.Colon,
]);

const CLOSERS: ReadonlySet<TokenKind> = new Set<TokenKind>([
  TokenKind.CloseBrace,
  TokenKind.CloseBracket,
]);

const BOM = '\uFEFF';

/** `'` and `"` have look-alikes that a word processor inserts without being asked. */
const SMART_QUOTES = /[\u2018\u2019\u201C\u201D]/;

/** A bare word that would be a legal identifier in JavaScript, and therefore a likely key. */
const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

/** Python's three literals and the JSON they were meant to be. */
const PYTHON_LITERALS: Readonly<Record<string, string>> = {
  True: 'true',
  False: 'false',
  None: 'null',
};

/** The eight legal single-character escapes of RFC 8259 §7. */
const SIMPLE_ESCAPES = '"\\/bfnrt';

/**
 * Continuation of a number that the scanner split in two.
 *
 * `01` arrives as `Number("0") Number("1")`, `0xFF` as `Number("0") Unknown("xFF")` and
 * `1.2.3` as `Number("1.2") Unknown(".3")` — all measured. A following `Unknown` only
 * belongs to the number when it *looks* like one, so `{"a":1;}` stays a stray `;` and does
 * not become an invalid number literal.
 */
const NUMERIC_CONTINUATION = /^[0-9A-Za-z.]/;

function textOf(source: string, token: Token): string {
  return source.slice(token.from, token.to);
}

function spanOf(token: Token): Span {
  return { from: token.from, to: token.to };
}

/**
 * Problems inside one string literal, found by walking the literal's own span.
 *
 * Run only when the scanner already flagged the token, so the cost is paid on broken
 * documents and never on the string-heavy valid ones. Walking it ourselves is what turns
 * jsonc's single `InvalidEscapeCharacter` for the whole token into one diagnostic per
 * offending escape, anchored on the escape.
 */
function scanStringBody(
  source: string,
  token: Token,
  emit: (draft: DiagnosticDraft) => void,
): number {
  const text = textOf(source, token);
  let found = 0;
  let i = 1; // Skip the opening quote; it is the one character we know is fine.

  while (i < text.length) {
    const ch = text[i] ?? '';

    if (ch === '\\') {
      const escape = text[i + 1] ?? '';

      if (escape === 'u') {
        // Report `\u` plus the characters that were clearly meant to be the four hex
        // digits — stopping at a quote or another backslash so the span never runs past
        // the escape the user typed. `"\u12"` reports `\u12`, `"\u12ZZ"` reports `\u12ZZ`.
        let end = i + 2;
        while (end < text.length && end < i + 6 && text[end] !== '"' && text[end] !== '\\') {
          end += 1;
        }
        const literal = text.slice(i, end);

        if (!/^\\u[0-9a-fA-F]{4}$/.test(literal)) {
          emit({
            code: 'string/invalid-unicode-escape',
            span: { from: token.from + i, to: token.from + end },
            params: { text: literal },
          });
          found += 1;
        }
        i = end;
        continue;
      }

      if (!SIMPLE_ESCAPES.includes(escape) || escape === '') {
        emit({
          code: 'string/invalid-escape',
          span: { from: token.from + i, to: Math.min(token.from + i + 2, token.to) },
          params: { ch: escape },
        });
        found += 1;
      }
      i += 2;
      continue;
    }

    if (ch.charCodeAt(0) < 0x20) {
      emit({
        code: 'string/control-character',
        span: { from: token.from + i, to: token.from + i + 1 },
        params: { hex: ch.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0') },
      });
      found += 1;
    }

    i += 1;
  }

  return found;
}

function checkString(input: LintInput, token: Token, emit: (draft: DiagnosticDraft) => void): void {
  if (token.scanError === ScanErrorKind.None) return;

  if (token.scanError === ScanErrorKind.UnterminatedString) {
    emit({ code: 'string/unterminated', span: spanOf(token) });
    return;
  }

  if (scanStringBody(input.source, token, emit) > 0) return;

  // The scanner saw something our walk did not. Report the token rather than stay silent:
  // a diagnostic on the whole literal is worse than one on the escape, and far better than
  // a document that lints clean and then refuses to parse.
  emit({
    code:
      token.scanError === ScanErrorKind.InvalidUnicode
        ? 'string/invalid-unicode-escape'
        : token.scanError === ScanErrorKind.InvalidCharacter
          ? 'string/control-character'
          : 'string/invalid-escape',
    span: spanOf(token),
    params: { text: textOf(input.source, token) },
  });
}

function checkComma(
  sig: readonly Token[],
  index: number,
  source: string,
  emit: (draft: DiagnosticDraft) => void,
): void {
  const comma = sig[index];
  if (comma === undefined) return;

  const previous = index > 0 ? sig[index - 1] : undefined;
  const next = sig[index + 1];

  if (previous === undefined || NO_VALUE_BEFORE.has(previous.kind)) {
    emit({ code: 'syntax/unexpected-comma', span: spanOf(comma) });
    return;
  }

  if (next !== undefined && CLOSERS.has(next.kind)) {
    emit({
      code: 'syntax/trailing-comma',
      span: spanOf(comma),
      params: { closer: source.slice(next.from, next.to) },
    });
  }
}

/**
 * The extent of a number the scanner split across several tokens, as a half-open index
 * range `[index, end)` into the significant stream.
 */
function numericRunEnd(sig: readonly Token[], index: number, source: string): number {
  let end = index + 1;

  while (end < sig.length) {
    const previous = sig[end - 1];
    const token = sig[end];
    if (previous === undefined || token === undefined) break;
    if (token.from !== previous.to) break;

    if (token.kind === TokenKind.Number) {
      end += 1;
      continue;
    }
    if (token.kind === TokenKind.Unknown && NUMERIC_CONTINUATION.test(textOf(source, token))) {
      end += 1;
      continue;
    }
    break;
  }

  return end;
}

function checkNumber(
  sig: readonly Token[],
  index: number,
  source: string,
  emit: (draft: DiagnosticDraft) => void,
): number {
  const first = sig[index];
  if (first === undefined) return index + 1;

  const end = numericRunEnd(sig, index, source);
  const last = sig[end - 1];
  if (last === undefined) return end;

  const span: Span = { from: first.from, to: last.to };
  const text = source.slice(span.from, span.to);

  if (end - index > 1) {
    if (/^-?0[xX]/.test(text)) {
      emit({ code: 'number/hex-literal', span, params: { text } });
      return end;
    }

    // THE WHOLE LITERAL, not just the zeros — the same span `hex-literal` above and
    // `invalid-literal` below use, and for the same three reasons. The message names the whole
    // literal (`{{text}}` is `-007`, never `00`); the repair rewrites the whole literal
    // (`fix-number-literal` proposes over `first.from … last.to`); and the span is what the
    // editor underlines, where a one-character wavy rule renders as a stray 8px tick rather
    // than as an underline — severity is carried by that line and a mark that reads as dirt
    // carries nothing. Narrowing to `0+` located the defect precisely and pointed at it in a
    // way nobody could see.
    if (/^-?0+[0-9]/.test(text)) {
      emit({ code: 'number/leading-zero', span, params: { text } });
      return end;
    }

    emit({ code: 'number/invalid-literal', span, params: { text } });
    return end;
  }

  if (first.scanError === ScanErrorKind.None) return end;

  if (text.endsWith('.')) {
    emit({
      code: 'number/trailing-decimal-point',
      span: { from: span.to - 1, to: span.to },
      params: { text },
    });
    return end;
  }

  const exponent = /[eE][+-]?$/.exec(text);
  if (exponent !== null) {
    emit({
      code: 'number/incomplete-exponent',
      span: { from: span.from + exponent.index, to: span.to },
      params: { text },
    });
    return end;
  }

  emit({ code: 'number/invalid-literal', span, params: { text } });
  return end;
}

/**
 * An `Unknown` token is everything the JSON grammar has no word for, so this is where the
 * dialects live: JavaScript, Python, a word processor's quotes, and a key someone forgot to
 * quote. Classification is ordered from most specific to least; the final fallback is
 * `syntax/unexpected-token`, which is honest rather than clever.
 *
 * Returns how many significant tokens it consumed — `-Infinity` arrives as two.
 */
function checkUnknown(
  sig: readonly Token[],
  index: number,
  source: string,
  emit: (draft: DiagnosticDraft) => void,
): number {
  const token = sig[index];
  if (token === undefined) return 1;

  const text = textOf(source, token);
  const next = sig[index + 1];
  const span = spanOf(token);

  if (token.from === 0 && text === BOM) {
    emit({ code: 'encoding/bom', span });
    return 1;
  }

  // The scanner splits `-Infinity` into `-` and `Infinity`; one mistake, one diagnostic.
  if (
    text === '-' &&
    next !== undefined &&
    next.kind === TokenKind.Unknown &&
    next.from === token.to &&
    textOf(source, next) === 'Infinity'
  ) {
    emit({
      code: 'number/non-finite',
      span: { from: token.from, to: next.to },
      params: { text: '-Infinity' },
    });
    return 2;
  }

  if (IDENTIFIER.test(text) && next !== undefined && next.kind === TokenKind.Colon) {
    emit({ code: 'key/unquoted', span, params: { text } });
    return 1;
  }

  if (text.length >= 2 && text.startsWith("'") && text.endsWith("'")) {
    emit({ code: 'string/single-quoted', span, params: { text } });
    return 1;
  }

  const smart = SMART_QUOTES.exec(text);
  if (smart !== null) {
    emit({
      code: 'string/smart-quoted',
      span: { from: token.from + smart.index, to: token.from + smart.index + 1 },
      params: { ch: smart[0] },
    });
    return 1;
  }

  if (text === 'undefined') {
    emit({ code: 'literal/js-undefined', span });
    return 1;
  }

  const python = PYTHON_LITERALS[text];
  if (python !== undefined) {
    emit({ code: 'literal/python-keyword', span, params: { text, json: python } });
    return 1;
  }

  if (text === 'NaN' || text === 'Infinity') {
    emit({ code: 'number/non-finite', span, params: { text } });
    return 1;
  }

  if (/^\+[0-9.]/.test(text)) {
    emit({
      code: 'number/leading-plus',
      span: { from: token.from, to: token.from + 1 },
      params: { text },
    });
    return 1;
  }

  if (/^\.[0-9]/.test(text)) {
    emit({
      code: 'number/leading-decimal-point',
      span: { from: token.from, to: token.from + 1 },
      params: { text },
    });
    return 1;
  }

  if (/^[-+.0-9]/.test(text)) {
    emit({ code: 'number/invalid-literal', span, params: { text } });
    return 1;
  }

  emit({ code: 'syntax/unexpected-token', span, params: { token: text } });
  return 1;
}

export const tokensCheck: LintCheck = {
  id: 'tokens',

  run(input, emit) {
    // Comments are trivia, so they are absent from the significant stream; they are also
    // the only thing here that the grammar walk below would never see.
    for (const token of input.tokens) {
      if (token.kind === TokenKind.LineComment || token.kind === TokenKind.BlockComment) {
        emit({ code: 'jsonc/comment', span: spanOf(token) });
      }
    }

    const sig = significantTokens(input.tokens);
    let index = 0;

    while (index < sig.length) {
      const token = sig[index];
      if (token === undefined || token.kind === TokenKind.EOF) break;

      switch (token.kind) {
        case TokenKind.Comma:
          checkComma(sig, index, input.source, emit);
          index += 1;
          break;
        case TokenKind.String:
          checkString(input, token, emit);
          index += 1;
          break;
        case TokenKind.Number:
          index = checkNumber(sig, index, input.source, emit);
          break;
        case TokenKind.Unknown:
          index += checkUnknown(sig, index, input.source, emit);
          break;
        default:
          index += 1;
          break;
      }
    }
  },
};
